import { computed, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { dsDtos } from '@vectronic/openApi-admin';
import { SchedulePreview, ScheduleTimpespanPreview, utcMoment } from '@vectronic/util';
import { cloneDeep } from 'es-toolkit';
import { Moment } from 'moment/moment';
import { BehaviorSubject, combineLatest, filter, map, startWith } from 'rxjs';

type CycleWindowMs = {
  startMs: number;
  startRangeMs: number;
  endRangeMs: number;
  intervalMs: number;
  startInterval: number;
  endInterval: number;
};

@Injectable({ providedIn: 'root' })
export class CameraScheduleService {
  rulesIndexes$ = new BehaviorSubject<number[]>([0]);
  defaultRulesIndexes$ = new BehaviorSubject<number[]>([0]);
  isRemovedSchedule$ = new BehaviorSubject(false);
  isFilledAndChanged$ = new BehaviorSubject(false);
  lastIndex = 0;
  rules$ = new BehaviorSubject<{ [key: number]: dsDtos.CameraScheduleRule }>({});
  defaultRules$ = new BehaviorSubject<{ [key: number]: dsDtos.CameraScheduleRule }>({});
  readonly isFilledAndChanged = signal(false);
  readonly hasDefaultValue = signal(false);
  isRuleExpanded: { [key: number]: boolean } = {};
  errors = signal<string[]>([]);
  hideCameraFormat = signal<boolean>(false);
  isScheduleExpanded = signal(false);
  rules = toSignal(this.rules$);
  isValid = computed(() => !this.errors()?.length);
  readonly isRemote = signal(false);

  isFilled = computed(() => {
    if (this.isEmptySchedule) return true;
    const rulesArray = Object.values(this.rules());
    if (!rulesArray?.length) return false;
    return rulesArray.every((rule) => this.isRuleFilled(rule));
  });

  readonly isChanged$ = combineLatest([
    this.rules$,
    this.defaultRules$,
    this.isFilledAndChanged$,
  ]).pipe(
    map(
      ([rules, defaultRules, isFilled]) =>
        JSON.stringify(defaultRules) !== JSON.stringify(rules) ||
        (this.isEmptySchedule && isFilled),
    ),
  );

  isChanged = toSignal(this.isChanged$);

  rulesUpdatedAndFilled$ = this.rules$.pipe(
    filter((rules) => {
      if (this.isEmptySchedule) return true;
      const rulesArray = Object.values(rules);
      for (const rule of rulesArray) if (this.isRuleFilled(rule)) return true;
      return false;
    }),
    startWith(undefined),
  );

  isRuleFilled({
    startTime,
    endTime,
    onTime,
    cycleTime,
    cameraScheduleRuleType,
    mainTriggerValue,
    cameraScheduleRuleMainTrigger,
    size,
    cameraFormatId,
  }: dsDtos.CameraScheduleRule) {
    return (
      startTime &&
      endTime &&
      onTime &&
      cycleTime &&
      cameraScheduleRuleType &&
      cameraScheduleRuleMainTrigger &&
      ((mainTriggerValue !== undefined && mainTriggerValue !== null) ||
        cameraScheduleRuleMainTrigger === 'HIGH_ACTIVITY') &&
      size &&
      (cameraFormatId !== undefined || this.hideCameraFormat())
    );
  }

  updateIsFilled(ruleIndex: number, ignoreExpand: boolean) {
    const isFilled =
      this.isValid() &&
      Object.values(this.rules$.value).every((rule) => {
        return rule && this.isRuleFilled(rule);
      });
    this.isFilledAndChanged.set(isFilled);
    this.isFilledAndChanged$.next(isFilled);
    if (!ignoreExpand) {
      this.isRuleExpanded[ruleIndex] = true;
    }
  }

  toggleRule(index: number) {
    this.isRuleExpanded[index] = !this.isRuleExpanded[index];
  }

  onClickAddRule() {
    this.lastIndex++;
    const rulesIndexes = this.rulesIndexes$.value;
    rulesIndexes.push(this.lastIndex);
    this.rulesIndexes$.next(rulesIndexes);
    const rules = cloneDeep(this.rules$.value);
    const start = utcMoment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    const end = utcMoment()
      .set({ hour: 23, minute: 59, second: 59, millisecond: 0 })
      .add({ year: 25 });
    rules[this.lastIndex] = {
      startTime: start.toISOString().split('.')[0] + 'Z',
      endTime: end.toISOString().split('.')[0] + 'Z',
      cycleTime: 1440 * 60,
      onTime: 1440 * 60,
      cameraScheduleRuleMainTrigger: 'TIMER',
      mainTriggerValue: 120,
      ruleOnlyValidOnProximityState: false,
      ruleOnlyValidOutsideVirtualFence: false,
      ruleOnlyValidVitNotExpelledState: false,
      ruleOnlyValidVitNoContactState: false,
      ruleOnlyValidVitContactState: false,
      ruleOnlyValidVitExpelledState: false,
      ruleOnlyValidInsideVirtualFence: false,
      ruleOnlyValidOnNoProximityState: false,
    } as dsDtos.CameraScheduleRule;
    this.rules$.next(rules);
    this.isRuleExpanded[this.lastIndex] = true;
    this.updateIsFilled(this.lastIndex, true);
  }

  updateRule(index: number, rule: dsDtos.CameraScheduleRule, readonly: boolean) {
    if (readonly) {
      return;
    }
    const rules = cloneDeep(this.rules$.value);
    rules[index] = rule;
    this.rules$.next(rules);
    this.updateIsFilled(index, readonly);
  }

  isPreviewable(index: number) {
    const rule = this.rules$.value?.[index];
    return this.isPreviewableRule(rule);
  }

  isTimeInterval(index: number) {
    const rule = this.rules$.value?.[index];
    return !rule.cameraScheduleRuleMainTrigger || rule.cameraScheduleRuleMainTrigger === 'TIMER';
  }

  isPreviewableRule(rule: dsDtos.CameraScheduleRule) {
    return Boolean(rule?.onTime && rule?.cycleTime);
  }

  shouldShowStartAndEndTimeRule({
    onTime,
    cycleTime,
    startTime,
    endTime,
  }: dsDtos.CameraScheduleRule) {
    return Boolean(onTime && cycleTime && startTime && endTime);
  }
  checkErrors() {
    const errors: string[] = [];
    const rules = this.rules$.value;
    Object.values(rules).forEach((rule, index) => {
      if (!this.isPreviewableRule(rule)) return;
      const { cameraScheduleRuleType, cycleTime, startTime, endTime } = rule;
      if (!cameraScheduleRuleType) return;
      if (cycleTime > 65535 * 60 || cycleTime < 0) {
        errors.push(`Repeat interval could be between 0 min to 65535 min for rule ${index + 1}`);
      }
      if (!this.isTimeValid(rule)) {
        errors.push(`Video length should not be greater than time interval for rule ${index + 1}!`);
      }
      if (!this.isOffsetValid(rule)) {
        errors.push(`Start time should not be greater than 24 hours for rule ${index + 1}!`);
      }
      if (!this.isPhotoIntervalValid(rule)) {
        errors.push(`Photo interval should be between 15 to 255 seconds for rule ${index + 1}`);
      }
      if (!this.isVideoLengthValid(rule)) {
        errors.push(`Video length should be between 1 to 65635 seconds for rule ${index + 1}`);
      }
      if (!this.isTimeIntervalValid(rule)) {
        errors.push(`Time interval should be between 1 to 65635 seconds for rule ${index + 1}`);
      }
      if (!this.isEndDateAfterStartDate(startTime, endTime)) {
        errors.push(`End date should be after start date for rule ${index + 1}`);
      }
    });
    this.errors.set(errors);
  }

  isEndDateAfterStartDate(startDate: string, endDate: string): boolean {
    return utcMoment(endDate).isSameOrAfter(utcMoment(startDate));
  }

  isOffsetValid(rule: dsDtos.CameraScheduleRule) {
    const startDate = utcMoment(rule.startTime);
    const offset = startDate.hours() * 60 + startDate.minutes();
    return offset < 1440;
  }

  isTimeValid({
    cameraScheduleRuleType,
    size,
    pictureInterval,
    mainTriggerValue,
    cameraScheduleRuleMainTrigger,
  }: dsDtos.CameraScheduleRule) {
    const length = cameraScheduleRuleType === 'VIDEO' ? size : (size - 1) * (pictureInterval || 0);
    const timeInterval = mainTriggerValue * 60;
    return length <= timeInterval || cameraScheduleRuleMainTrigger !== 'TIMER';
  }

  isTimeIntervalValid({
    mainTriggerValue,
    cameraScheduleRuleMainTrigger,
  }: dsDtos.CameraScheduleRule) {
    return (
      (mainTriggerValue >= 1 && mainTriggerValue <= 65635) ||
      cameraScheduleRuleMainTrigger !== 'TIMER'
    );
  }

  isPhotoIntervalValid({
    pictureInterval,
    size,
    cameraScheduleRuleType,
  }: dsDtos.CameraScheduleRule) {
    return (
      (pictureInterval >= 15 && pictureInterval <= 255) ||
      size === 1 ||
      cameraScheduleRuleType === 'VIDEO'
    );
  }

  isVideoLengthValid({ size, cameraScheduleRuleType }: dsDtos.CameraScheduleRule) {
    return (size >= 1 && size <= 65635) || cameraScheduleRuleType === 'PICTURE';
  }

  onRemoveRule(index: number, readonly: boolean) {
    if (readonly) {
      return;
    }
    const rulesIndexes = this.rulesIndexes$.value.filter((i) => i !== index);
    const rules = cloneDeep(this.rules$.value);
    const rulesArray = Object.values(rules);
    if (rulesArray.length === 1 && !this.isRemote()) {
      this.setEmptySchedule();
      return;
    }
    if (rulesArray.length === 1 && this.isRemote()) {
      this.onRemoveSchedule();
      return;
    }
    delete rules[index];
    this.rulesIndexes$.next(rulesIndexes);
    this.rules$.next(rules);
    this.updateIsFilled(this.lastIndex, true);
  }

  schedulesInHour(rule: dsDtos.CameraScheduleRule, date: Moment, hour: number) {
    const minute = 0;
    const second = 0;
    const millisecond = 0;
    const startRangeDate = utcMoment(date).set({ hour, minute, second, millisecond });
    hour++;
    const endRangeDate = utcMoment(date).set({ hour, minute, second, millisecond });
    return this.schedulesInRange(rule, startRangeDate, endRangeDate);
  }

  private getRange(date: Moment, of: 'month' | 'day') {
    return { start: utcMoment(date).startOf(of), end: utcMoment(date).endOf(of) };
  }

  numberOfSchedulesInMonth(rule: dsDtos.CameraScheduleRule, date: Moment) {
    const { start, end } = this.getRange(date, 'month');
    return this.numberOfSchedulesInRange(rule, start, end);
  }

  schedulesInMonth(rule: dsDtos.CameraScheduleRule, date: Moment) {
    const { start, end } = this.getRange(date, 'month');
    return this.schedulesInRange(rule, start, end);
  }

  numberOfSchedulesInDay(rule: dsDtos.CameraScheduleRule, date: Moment) {
    const { start, end } = this.getRange(date, 'day');
    return this.numberOfSchedulesInRange(rule, start, end);
  }

  numberOfTimespansInDay(rule: dsDtos.CameraScheduleRule, date: Moment): number {
    return this.timespanInDay(rule, date).length > 0 ? 1 : 0;
  }

  numberOfTimespansInMonth(rule: dsDtos.CameraScheduleRule, date: Moment): number {
    const daysInMonth = utcMoment(date).daysInMonth();
    let count = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const day = utcMoment(date).set('date', d);
      if (this.timespanInDay(rule, day).length > 0) count++;
    }
    return count;
  }

  schedulesInDay(rule: dsDtos.CameraScheduleRule, date: Moment) {
    const { start, end } = this.getRange(date, 'day');
    return this.schedulesInRange(rule, start, end);
  }

  isNonTimerTrigger(rule: dsDtos.CameraScheduleRule): boolean {
    return !!rule?.cameraScheduleRuleMainTrigger && rule.cameraScheduleRuleMainTrigger !== 'TIMER';
  }

  timespanInDay(rule: dsDtos.CameraScheduleRule, date: Moment): ScheduleTimpespanPreview[] {
    const { start, end } = this.getRange(date, 'day');
    return this.timespanInRange(rule, start, end);
  }

  getTriggerLabel(trigger: dsDtos.CameraScheduleRule.CameraScheduleRuleMainTriggerEnum): string {
    switch (trigger) {
      case 'GPS':
        return 'GPS';
      case 'VIT':
        return 'VIT';
      case 'PROXIMITY_PRELIMANARY':
        return 'Proximity';
      case 'HIGH_ACTIVITY':
        return 'High Activity';
      default:
        return trigger ?? 'Unknown';
    }
  }

  private timespanInRange(
    rule: dsDtos.CameraScheduleRule,
    startRange: Moment,
    endRange: Moment,
  ): ScheduleTimpespanPreview[] {
    const timespans: ScheduleTimpespanPreview[] = [];
    if (!rule || !this.isPreviewableRule(rule)) return timespans;
    if (!this.isNonTimerTrigger(rule)) return timespans;
    const { startTime, endTime, cycleTime, onTime } = rule;
    const triggerLabel = this.getTriggerLabel(rule.cameraScheduleRuleMainTrigger);
    const startMs = utcMoment(startTime, true).valueOf();
    const endMs = utcMoment(endTime, true).valueOf();
    const startRangeMs = startRange.valueOf();
    const endRangeMs = endRange.valueOf();
    if (startRangeMs > endMs || endRangeMs < startMs) return timespans;
    const intervalMs = this.minToMs(cycleTime / 60);
    const onTimeMs = this.minToMs(onTime / 60);
    const startInterval = this.positive(Math.floor((startRangeMs - startMs) / intervalMs));
    const endInterval = Math.min(
      Math.ceil((endRangeMs - startMs) / intervalMs),
      Math.ceil((endMs - startMs) / intervalMs),
    );
    const rangeDurationMs = endRangeMs - startRangeMs;
    const lengthMin = rangeDurationMs / (60 * 1000);

    // start one interval earlier so on-time windows overlapping into the next cycle are covered
    for (let i = Math.max(0, startInterval - 1); i < endInterval; i++) {
      const activeStartMs = startMs + i * intervalMs;
      const activeEndMs = activeStartMs + onTimeMs;
      const clampedStart = Math.max(activeStartMs, startRangeMs);
      const clampedEnd = Math.min(activeEndMs, endRangeMs);
      if (clampedEnd <= clampedStart) continue;
      const offsetMin = (clampedStart - startRangeMs) / (60 * 1000);
      const durationMin = (clampedEnd - clampedStart) / (60 * 1000);
      timespans.push({ offset: offsetMin, duration: durationMin, length: lengthMin, triggerLabel });
    }
    return timespans;
  }

  private minToMs(mins: number) {
    return mins * 60 * 1000;
  }

  private positive(n: number) {
    return Math.max(n, 0);
  }

  /**
   * Computes the repeat-interval (cycleTime) window boundaries that overlap the given range.
   * This only depends on startTime/endTime/cycleTime, not on the trigger type, so it is shared
   * by every trigger-specific fix calculation (e.g. TIMER ticks, GPS fixes).
   */
  private getCycleWindowMs(
    rule: dsDtos.CameraScheduleRule,
    startRange: Moment,
    endRange: Moment,
  ): CycleWindowMs | undefined {
    if (!rule || !this.isPreviewableRule(rule)) return;
    const { startTime, endTime, cycleTime } = rule;
    const startMs = utcMoment(startTime, true).valueOf();
    const endMs = utcMoment(endTime, true).valueOf();
    const startRangeMs = startRange.valueOf();
    const endRangeMs = endRange.valueOf();
    if (startRangeMs > endMs || endRangeMs < startMs) return;
    const intervalMs = this.minToMs(cycleTime / 60);
    const startInterval = this.positive(Math.floor((startRangeMs - startMs) / intervalMs));
    const endInterval = Math.min(
      Math.ceil((endRangeMs - startMs) / intervalMs),
      Math.ceil((endMs - startMs) / intervalMs),
    );
    return { startMs, startRangeMs, endRangeMs, intervalMs, startInterval, endInterval };
  }

  private getMs(rule: dsDtos.CameraScheduleRule, startRange: Moment, endRange: Moment) {
    if (!rule) return;
    const { cameraScheduleRuleMainTrigger, mainTriggerValue } = rule;
    if (cameraScheduleRuleMainTrigger !== 'TIMER' || !mainTriggerValue) return;
    return this.getCycleWindowMs(rule, startRange, endRange);
  }

  /**
   * Whether a given fix timestamp falls inside one of the rule's active (onTime) windows,
   * i.e. within `onTime` of the start of one of its `cycleTime` repeat intervals.
   */
  private isInActiveWindow(
    millis: number,
    { startMs, intervalMs, startInterval, endInterval }: CycleWindowMs,
    onTimeMs: number,
  ): boolean {
    const cycleIndex = Math.floor((millis - startMs) / intervalMs);
    if (cycleIndex < startInterval || cycleIndex >= endInterval) return false;
    const offsetInCycleMs = millis - (startMs + cycleIndex * intervalMs);
    return offsetInCycleMs < onTimeMs;
  }

  /**
   * Calculates camera fixes for a GPS-triggered rule (`cameraScheduleRuleMainTrigger === 'GPS'`)
   * within the given range.
   *
   * Unlike TIMER (which generates its own fix times from `mainTriggerValue`), the fix timestamps
   * here come from the GPS schedule (`gpsFixesMillis`, supplied by the caller). Only the camera
   * schedule's repeat interval (`cycleTime`) and on-time window (`onTime`) are used to decide
   * which of those GPS fixes fall within an active camera window.
   *
   * Note: this is only used by the airtime/lifetime calculator for now, it does not affect the
   * camera calendar view or preview.
   */
  schedulesFromGpsFixesInRange(
    rule: dsDtos.CameraScheduleRule,
    startRange: Moment,
    endRange: Moment,
    gpsFixesMillis: number[],
  ): SchedulePreview[] {
    const schedules: SchedulePreview[] = [];
    if (rule?.cameraScheduleRuleMainTrigger !== 'GPS' || !gpsFixesMillis?.length) return schedules;
    const window = this.getCycleWindowMs(rule, startRange, endRange);
    if (!window) return schedules;
    const { startRangeMs, endRangeMs } = window;
    const onTimeMs = this.minToMs(rule.onTime / 60);
    for (const millis of gpsFixesMillis) {
      if (millis < startRangeMs || millis >= endRangeMs) continue;
      if (this.isInActiveWindow(millis, window, onTimeMs)) {
        schedules.push({ millis, rule });
      }
    }
    return schedules;
  }

  schedulesFromGpsFixesInDay(
    rule: dsDtos.CameraScheduleRule,
    date: Moment,
    gpsFixesMillis: number[],
  ): SchedulePreview[] {
    const { start, end } = this.getRange(date, 'day');
    return this.schedulesFromGpsFixesInRange(rule, start, end, gpsFixesMillis);
  }

  numberOfSchedulesFromGpsFixesInDay(
    rule: dsDtos.CameraScheduleRule,
    date: Moment,
    gpsFixesMillis: number[],
  ): number {
    return this.schedulesFromGpsFixesInDay(rule, date, gpsFixesMillis).length;
  }

  private numberOfSchedulesInRange(
    rule: dsDtos.CameraScheduleRule,
    startRange: Moment,
    endRange: Moment,
  ) {
    let schedulesCount = 0;
    const ms = this.getMs(rule, startRange, endRange);
    if (!ms) return schedulesCount;
    const { startMs, startRangeMs, endRangeMs, intervalMs, startInterval, endInterval } = ms;
    // start one interval earlier so on-time windows overlapping into the next cycle are covered
    for (let i = Math.max(0, startInterval - 1); i < endInterval; i++) {
      const startIntervalMs = startMs + i * intervalMs;
      const onTimeMs = this.minToMs(rule.onTime / 60);
      const mainTriggerMs = this.minToMs(rule.mainTriggerValue);
      let scheduleMs = startIntervalMs;
      while (scheduleMs < startIntervalMs + onTimeMs) {
        if (scheduleMs >= startRangeMs && scheduleMs < endRangeMs) schedulesCount++;
        scheduleMs += mainTriggerMs;
      }
    }
    return schedulesCount;
  }

  private schedulesInRange(rule: dsDtos.CameraScheduleRule, startRange: Moment, endRange: Moment) {
    const schedules: SchedulePreview[] = [];
    const ms = this.getMs(rule, startRange, endRange);
    if (!ms) return schedules;
    const { startMs, startRangeMs, endRangeMs, intervalMs, startInterval, endInterval } = ms;
    // start one interval earlier so on-time windows overlapping into the next cycle are covered
    for (let i = Math.max(0, startInterval - 1); i < endInterval; i++) {
      const startIntervalMs = startMs + i * intervalMs;
      const onTimeMs = this.minToMs(rule.onTime / 60);
      const mainTriggerMs = this.minToMs(rule.mainTriggerValue);
      let scheduleMs = startIntervalMs;
      while (scheduleMs < startIntervalMs + onTimeMs) {
        if (scheduleMs >= startRangeMs && scheduleMs < endRangeMs) {
          schedules.push({ millis: scheduleMs, rule });
        }
        scheduleMs += mainTriggerMs;
      }
    }
    return schedules;
  }

  get isOneRuleExpanded() {
    return Object.values(this.isRuleExpanded).includes(true);
  }

  onRemoveSchedule() {
    this.isFilledAndChanged.set(false);
    this.isFilledAndChanged$.next(false);
    this.hasDefaultValue.set(false);
    this.isScheduleExpanded.set(false);
    this.rules$.next({ 0: {} });
    this.rulesIndexes$.next([0]);
    this.isRuleExpanded = {};
    this.lastIndex = 0;
    this.isRemovedSchedule$.next(true);
    this.isRemovedSchedule$.next(false);
  }

  removeDefaultSchedule() {
    this.defaultRules$.next({ 0: {} });
    this.defaultRulesIndexes$.next([0]);
  }

  setToDefaultSchedule() {
    this.isFilledAndChanged$.next(false);
    this.isFilledAndChanged.set(false);
    if (Object.values(this.defaultRules$.value).length) {
      this.rules$.next(cloneDeep(this.defaultRules$.value));
      this.rulesIndexes$.next(cloneDeep(this.defaultRulesIndexes$.value));
    }
    if (
      Object.values(this.defaultRules$.value).length &&
      Object.values(this.defaultRules$.value[0]).length
    ) {
      this.isScheduleExpanded.set(true);
    } else {
      this.isScheduleExpanded.set(false);
    }
  }

  setEmptySchedule() {
    this.isScheduleExpanded.set(false);
    this.isFilledAndChanged.set(true);
    this.isFilledAndChanged$.next(true);
    this.rules$.next({});
    this.rulesIndexes$.next([]);
    this.lastIndex = -1;
  }

  get isEmptySchedule() {
    const rules = Object.values(this.rules$.value);
    return rules?.length === 0;
  }

  onAddSchedule() {
    if (this.isEmptySchedule) {
      this.onRemoveSchedule();
    }
    this.isScheduleExpanded.set(true);
    this.isRuleExpanded[0] = true;
  }

  loadRules(rules: dsDtos.CameraScheduleRule[]) {
    if (!rules) return;
    const rulesObj: { [key: number]: dsDtos.CameraScheduleRule } = {};
    rules.forEach((rule, index) => {
      rulesObj[index] = rule;
      this.isRuleExpanded[index] = true;
    });
    this.rules$.next(rulesObj);
    this.rulesIndexes$.next(Object.keys(rulesObj).map(Number));
    this.lastIndex = rules.length - 1;
    this.isScheduleExpanded.set(true);
    this.isFilledAndChanged.set(true);
    this.isFilledAndChanged$.next(true);
    this.updateIsFilled(this.lastIndex, true);
  }

  toSchedule(): dsDtos.Schedule {
    return {
      scheduleType: 'CAMERA',
      userDefined: true,
      cameraScheduleRules: Object.values(this.rules$.value).filter(Boolean),
    } as dsDtos.Schedule;
  }
}
